/**
 * Sidebar — brand, workspace navigation, the chat list, and the account
 * control in one full-height column.
 *
 * Every nav row carries a tinted icon chip, so the eye separates Pipelines from
 * Members by colour and shape before reading a word. Collapsing
 * leaves a 64px icon rail; the choice persists.
 */

import { useMemo, useState } from "react";
import type { Session } from "../api";
import type { Skin } from "../skins";
import { SessionList } from "./SessionList";
import { AccountMenu } from "./AccountMenu";
import {
  IconBrand, IconKey, IconMembers, IconPanelLeft, IconPipelines, IconPlus, IconSearch,
} from "./icons";

export interface NavSpec {
  key: string;
  label: string;
  icon: JSX.Element;
  /** Palette the icon chip is tinted with. */
  tone: "accent" | "violet" | "running" | "done" | "neutral";
  count?: number;
  active?: boolean;
  disabled?: boolean;
  title?: string;
  onClick?: () => void;
  href?: string;
}

const TINT: Record<NavSpec["tone"], [string, string]> = {
  accent: ["var(--accent-tint)", "var(--accent)"],
  violet: ["var(--violet-tint)", "var(--violet)"],
  running: ["var(--orange-tint)", "var(--orange)"],
  done: ["var(--green-tint)", "var(--green)"],
  neutral: ["var(--hover-2)", "var(--ink-2)"],
};

function NavRow({ spec }: { spec: NavSpec }) {
  const [tint, tone] = TINT[spec.tone];
  const inner = (
    <>
      <span className="nav-glyph" style={{ "--tint": tint, "--tone": tone } as React.CSSProperties}>
        {spec.icon}
      </span>
      <span className="nav-text">{spec.label}</span>
      {spec.count ? <span className="nav-count">{spec.count}</span> : null}
    </>
  );
  const title = spec.title ?? spec.label;
  if (spec.href) {
    return (
      <a className="nav-item" href={spec.href} target="_blank" rel="noreferrer" title={title}>
        {inner}
      </a>
    );
  }
  return (
    <button
      className={`nav-item${spec.active ? " active" : ""}`}
      onClick={spec.onClick}
      disabled={spec.disabled}
      title={title}
    >
      {inner}
    </button>
  );
}

export function Sidebar({
  instance, skin, sessions, currentSid, collapsed, writeable, pipelinesActive,
  onToggleCollapse, onNewChat, onSelect, onDelete,
  onPipelines, onMembers, onClients,
}: {
  instance: string;
  skin: Skin | null;
  sessions: Session[];
  currentSid?: string;
  collapsed: boolean;
  writeable: boolean;
  pipelinesActive: boolean;
  onToggleCollapse: () => void;
  onNewChat: () => void;
  onSelect: (s: Session) => void;
  onDelete: (s: Session) => void;
  onPipelines: () => void;
  onMembers: () => void;
  /** Present for admins and owners; the API clients entry is hidden otherwise. */
  onClients?: () => void;
}) {
  const [filter, setFilter] = useState("");

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter(s => (s.title || "").toLowerCase().includes(q));
  }, [sessions, filter]);

  const nav: NavSpec[] = writeable ? [
    {
      key: "pipelines", label: "Pipelines", icon: <IconPipelines size={14} />, tone: "accent",
      onClick: onPipelines, active: pipelinesActive,
      title: "Generate documents",
    },
    {
      key: "members", label: "Members", icon: <IconMembers size={14} />, tone: "neutral",
      onClick: onMembers, title: "View members; owners invite and set roles",
    },
    ...(onClients ? [{
      key: "clients", label: "API clients", icon: <IconKey size={14} />, tone: "violet" as const,
      onClick: onClients, title: "Credentials for servers that call this instance",
    }] : []),
  ] : [];

  return (
    <aside className="sidebar">
      <div className="sb-head">
        <span className="sb-brand">
          <span className="sb-mark"><IconBrand size={17} /></span>
          <span className="sb-wordmark">
            {skin ? skin.brand : "Nexus"}
            <span className="sb-sub">{skin ? skin.tag : instance}</span>
          </span>
        </span>
        <button
          className="sb-collapse"
          onClick={onToggleCollapse}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <IconPanelLeft size={15} />
        </button>
      </div>

      <button className="sb-new primary" onClick={onNewChat} title="New chat">
        <IconPlus size={15} />
        <span>New chat</span>
      </button>

      <div className="sb-scroll">
        {nav.length > 0 && (
          <>
            <div className="sb-label">Workspace</div>
            {nav.map(n => <NavRow key={n.key} spec={n} />)}
          </>
        )}

        <div className="sb-label">Chats</div>
        {sessions.length > 6 && (
          <div className="sb-search">
            <IconSearch size={13} />
            <input
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Filter chats"
              aria-label="Filter chats"
            />
          </div>
        )}
        <SessionList
          instance={instance}
          sessions={shown}
          currentSid={currentSid}
          onSelect={onSelect}
          onDelete={onDelete}
          emptyLabel={filter ? "No chat matches that." : undefined}
        />
      </div>

      <div className="sb-foot">
        <AccountMenu collapsed={collapsed} />
      </div>
    </aside>
  );
}
